'use client'

import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import ReactTimeAgo from 'react-time-ago'

import type { RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import { Avatar, AvatarFallback, AvatarImage, DataTableColumnHeader } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

export interface GetColumnsProps {
  type: 'school' | 'web'
  renderLink: RenderLink
  periodDays: number
}

type Data = RouterOutputs['metrics']['getReadingTrend']['trends'][number]

function formatConsistencyPct(activeDays: number, periodDays: number): string {
  if (activeDays <= 0) return '-'
  const pct = periodDays > 0 ? (activeDays / periodDays) * 100 : 0
  return `${pct.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`
}

export function formatStaminaPerSession(averageDurationSeconds: number, sessionCount: number): string {
  if (sessionCount <= 0 || averageDurationSeconds <= 0) return '-'
  const minutes = averageDurationSeconds / 60
  const formatted =
    minutes < 1
      ? minutes.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
      : minutes.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  return `${formatted} mnt/sesi`
}

export function useColumns({ type, renderLink, periodDays }: GetColumnsProps): ColumnDef<Data>[] {
  return useMemo(
    () => [
      {
        id: 'id',
        accessorKey: 'Siswa',
        filterFn: (row, _columnId, filterValue: string) =>
          row.original.name.toLowerCase().includes(filterValue.toLowerCase()),
        sortingFn: (rowA, rowB) => rowA.original.name.localeCompare(rowB.original.name),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Murid' />,
        cell: ({ row }) => {
          return (
            <div className='srd:flex srd:items-center srd:gap-x-2 srd:flex-nowrap srd:shrink-0 srd:w-[250px] srd:truncate'>
              <Avatar profile='user' className='srd:size-10'>
                <AvatarImage src={row.original.image ?? undefined} />
                <AvatarFallback>{getInitials(row.original.name)}</AvatarFallback>
              </Avatar>

              <div className='srd:flex srd:flex-col srd:min-w-0'>
                {renderLink({
                  children: row.original.name,
                  className: cn(typographyVariants({ variant: 'body-lg' }), 'srd:hover:underline srd:truncate'),
                  meta: {
                    childId: row.original.id,
                    back_redirect: 'reading-trend',
                  },
                })}
                <span className={cn(typographyVariants({ variant: 'body-sm' }))}>
                  {row.original.lastActiveAt ? (
                    <>
                      Aktif{' '}
                      <ReactTimeAgo
                        className={cn(typographyVariants({ variant: 'body-sm' }))}
                        date={new Date(row.original.lastActiveAt)}
                      />
                    </>
                  ) : (
                    'Belum pernah login'
                  )}
                </span>
              </div>
            </div>
          )
        },
      },
      {
        id: 'consistency',
        accessorFn: (row) => (periodDays > 0 ? row.activeDays / periodDays : 0),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Konsistensi' />,
        cell: ({ row }) => (
          <div className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-nowrap')}>
            {formatConsistencyPct(row.original.activeDays, periodDays)}
          </div>
        ),
      },
      {
        id: 'averageDurationSeconds',
        accessorFn: (row) => row.averageDurationSeconds,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Stamina' />,
        cell: ({ row }) => (
          <div className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-nowrap')}>
            {formatStaminaPerSession(row.original.averageDurationSeconds, row.original.sessionCount)}
          </div>
        ),
      },
      {
        id: 'completionRate',
        accessorFn: (row) => (row.totalBooksRead > 0 ? row.completedBooksCount / row.totalBooksRead : 0),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Selesai' />,
        cell: ({ row }) => {
          const { completedBooksCount, totalBooksRead } = row.original
          if (totalBooksRead <= 0)
            return <div className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-nowrap')}>-</div>
          const pct = (completedBooksCount / totalBooksRead) * 100
          return (
            <div className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-nowrap')}>
              {pct.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%
            </div>
          )
        },
      },
      {
        id: 'sessionCount',
        accessorFn: (row) => row.sessionCount,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Sesi' />,
        cell: ({ row }) => (
          <div className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-nowrap')}>
            {row.original.sessionCount > 0 ? row.original.sessionCount : '-'}
          </div>
        ),
      },
      {
        id: 'totalBooksRead',
        accessorFn: (row) => row.totalBooksRead,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Buku dibuka' />,
        cell: ({ row }) => (
          <div className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-nowrap')}>
            {row.original.totalBooksRead > 0 ? row.original.totalBooksRead : '-'}
          </div>
        ),
      },
    ],
    [type, renderLink, periodDays],
  )
}
